/**
 * Input Component - Reusable text input for feature pages
 */

import React, { useId } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

const Input = React.forwardRef(
  (
    {
      id: idProp,
      label,
      placeholder = '',
      value,
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      readOnly = false,
      error = false,
      errorMessage = '',
      required = false,
      icon,
      iconPosition = 'left',
      size = 'md',
      type = 'text',
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = idProp || generatedId;
    const errorId = `${inputId}-error`;
    const { semantic, controlHeights, radiusPx, shadows, typography } = useFeatureTheme();
    const [isFocused, setIsFocused] = React.useState(false);

    const sizeStyles = {
      sm: { height: controlHeights.sm, fontSize: typography.fontSizeSm },
      md: { height: controlHeights.md, fontSize: typography.fontSizeMd },
      lg: { height: controlHeights.lg, fontSize: typography.fontSizeBase },
    };

    let borderColor = semantic.borders.border;
    let backgroundColor = semantic.surfaces.surface;

    if (readOnly) {
      backgroundColor = semantic.surfaces.surfaceMuted;
    } else if (error) {
      borderColor = semantic.states.danger;
    } else if (isFocused) {
      borderColor = semantic.interactive.primary;
    }

    return (
      <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: '4px', ...sx }}>
        {label && (
          <Typography
            component="label"
            htmlFor={inputId}
            sx={{
              fontSize: typography.fontSizeSm,
              fontWeight: typography.fontWeightMedium,
              color: semantic.text.secondary,
            }}
          >
            {label}
            {required ? ' *' : ''}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            px: '12px',
            border: `1px solid ${borderColor}`,
            borderRadius: radiusPx.md,
            backgroundColor,
            transition: 'all 160ms ease',
            boxShadow: isFocused ? shadows.focus : 'none',
            ...sizeStyles[size],
          }}
        >
          {icon && iconPosition === 'left' && (
            <Box sx={{ display: 'flex', color: semantic.text.muted }}>{icon}</Box>
          )}

          <InputBase
            ref={ref}
            id={inputId}
            fullWidth
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={(event) => {
              setIsFocused(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              setIsFocused(false);
              onBlur?.(event);
            }}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={error || undefined}
            aria-describedby={error && errorMessage ? errorId : undefined}
            slotProps={{
              input: {
                style: {
                  color: disabled || readOnly ? semantic.text.muted : semantic.text.primary,
                  padding: 0,
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                },
              },
            }}
            sx={{
              '& input::placeholder': {
                color: semantic.text.muted,
                opacity: 1,
              },
            }}
            {...props}
          />

          {icon && iconPosition === 'right' && (
            <Box sx={{ display: 'flex', color: semantic.text.muted }}>{icon}</Box>
          )}
        </Box>

        {error && errorMessage && (
          <Typography id={errorId} role="alert" sx={{ fontSize: typography.fontSizeXs, color: semantic.states.danger }}>
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }
);

Input.displayName = 'Input';

export default Input;
